import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useGetContractorsQuery,
  useCreateContractorMutation,
  useUpdateContractorMutation,
  useDeleteContractorMutation,
} from './contractorsApiSlice';
import { Icon, Table, Loader } from 'semantic-ui-react';
import moment from 'moment';
import { NAV } from '../../navLabels';
import {
  ui,
  Pill,
  Badge,
  Modal,
  InfoNote,
  fieldStyle,
  labelStyle,
} from '../../components/adminUi';

const ContractorModal = ({ open, onClose, contractor = null }) => {
  const [createContractor] = useCreateContractorMutation();
  const [updateContractor] = useUpdateContractorMutation();
  const [name, setName] = useState(contractor?.name || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(contractor?.name || '');
  }, [contractor, open]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (contractor) {
        await updateContractor({ id: contractor.id, name }).unwrap();
      } else {
        await createContractor({ name }).unwrap();
      }
      onClose();
    } catch (err) {
      console.error('Failed to save contractor:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={contractor ? '파트너 수정' : '새 파트너 추가'}
      footer={
        <>
          <Pill variant="ghost" onClick={onClose} disabled={saving}>취소</Pill>
          <Pill variant="primary" onClick={handleSubmit} disabled={saving || !name.trim()}>
            {saving ? '저장 중…' : '저장'}
          </Pill>
        </>
      }
    >
      <label style={labelStyle}>파트너 이름</label>
      <input
        style={fieldStyle}
        value={name}
        autoFocus
        onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && name.trim() && handleSubmit()}
      />
    </Modal>
  );
};

const ContractorManagement = () => {
  const navigate = useNavigate();
  const currentUser = useSelector(state => state.auth.user);
  const isSuperuser = currentUser?.is_superuser;
  const { data, isLoading } = useGetContractorsQuery();
  const [deleteContractor] = useDeleteContractorMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const contractors = Array.isArray(data) ? data : data?.results || [];

  const handleEdit = (contractor) => {
    setEditingContractor(contractor);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditingContractor(null);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (confirmDelete) {
      try {
        await deleteContractor(confirmDelete.id).unwrap();
      } catch (err) {
        console.error('Failed to delete contractor:', err);
      }
      setConfirmDelete(null);
    }
  };

  if (isLoading) return <Loader active />;

  const headerCellStyle = { borderBottom: `1px solid ${ui.headerBorder}`, color: ui.muted, fontWeight: 600 };
  const colSpan = isSuperuser ? 6 : 5;

  return (
    <div style={{ height: 'auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header Section */}
      <div
        style={{
          flex: '0 0 auto',
          padding: '16px 0',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#F2F2F7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon name="building" style={{ margin: 0, color: ui.accent, fontSize: '18px' }} />
            </div>
            <div>
              <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, color: ui.text }}>
                {isSuperuser ? NAV.CONTRACTORS : NAV.CONTRACTOR_ACCESS}
              </h2>
              <p style={{ fontSize: '13px', color: ui.muted, margin: 0 }}>
                {isSuperuser ? '파트너 현황 및 관리' : '초대 링크 및 관리자 권한'}
              </p>
            </div>
          </div>
          {isSuperuser && (
            <Pill variant="primary" onClick={handleCreate} style={{ height: '40px', padding: '0 20px' }}>
              <Icon name="plus" style={{ margin: 0 }} /> 새 파트너
            </Pill>
          )}
        </div>
      </div>

      <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <InfoNote>
          <Icon name="info circle" style={{ margin: 0, color: ui.accent, marginTop: '1px' }} />
          {/* The click-through hint used to be shown only to superusers, which is backwards:
              a 파트너 관리자 sees exactly one row and the only reason they are on this page
              is what lies behind it (초대 링크, 관리자 지정). They are the ones who need it. */}
          <span>
            {isSuperuser
              ? '파트너별로 소속 사용자·관리자를 확인하고, 페르소나를 배정할 수 있습니다. 행을 클릭하면 파트너 상세로 이동해 관리자와 초대 링크를 관리할 수 있습니다.'
              : `회사 이름을 클릭하면 직원 초대 링크를 발급하고 파트너 관리자를 지정할 수 있습니다. 직원별 학습 현황과 가입 승인은 ‘${NAV.MY_STAFF}’에서 확인하세요.`}
          </span>
        </InfoNote>

        <div style={{
          display: 'flex', flexDirection: 'column', backgroundColor: 'white', borderRadius: '12px',
          border: `1px solid ${ui.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflowY: 'auto',
        }}>
          <Table compact="very" selectable style={{ margin: 0, border: 'none' }}>
            <Table.Header style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white' }}>
              <Table.Row>
                <Table.HeaderCell style={{ ...headerCellStyle, paddingLeft: '20px' }}>이름</Table.HeaderCell>
                <Table.HeaderCell style={headerCellStyle}>사용자 수</Table.HeaderCell>
                <Table.HeaderCell style={headerCellStyle}>관리자 수</Table.HeaderCell>
                {/* The cell below renders `persona_count`; the header said 파트너 #, which
                    named neither the value nor anything else on this screen. */}
                <Table.HeaderCell style={headerCellStyle}>페르소나 수</Table.HeaderCell>
                <Table.HeaderCell style={headerCellStyle}>생성일</Table.HeaderCell>
                {isSuperuser && <Table.HeaderCell style={headerCellStyle} textAlign="right">작업</Table.HeaderCell>}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {contractors.map((contractor) => (
                <Table.Row
                  key={contractor.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/contractors/${contractor.id}`)}
                >
                  <Table.Cell style={{ paddingLeft: '20px' }}>
                    <span style={{ color: ui.accent, fontWeight: 600 }}>{contractor.name}</span>
                  </Table.Cell>
                  <Table.Cell>{contractor.user_count ?? '-'}</Table.Cell>
                  <Table.Cell>{contractor.admin_count ?? '-'}</Table.Cell>
                  <Table.Cell>
                    {contractor.persona_count > 0
                      ? <Badge tone="blue">{contractor.persona_count}</Badge>
                      : <span style={{ color: '#999' }}>-</span>}
                  </Table.Cell>
                  <Table.Cell style={{ color: ui.muted }}>{moment(contractor.created_at).format('YYYY-MM-DD')}</Table.Cell>
                  {isSuperuser && (
                    <Table.Cell textAlign="right" onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        <Pill variant="neutral" style={{ padding: '7px 10px' }} onClick={() => handleEdit(contractor)} title="수정">
                          <Icon name="edit" style={{ margin: 0 }} />
                        </Pill>
                        <Pill variant="danger" style={{ padding: '7px 10px' }} onClick={() => setConfirmDelete(contractor)} title="삭제">
                          <Icon name="trash" style={{ margin: 0 }} />
                        </Pill>
                      </div>
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
              {contractors.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={colSpan} textAlign="center" style={{ padding: '48px', color: ui.muted }}>
                    <Icon name="building outline" size="big" style={{ color: '#d0d0d5' }} />
                    <div style={{ marginTop: '10px', fontSize: '14px' }}>등록된 파트너가 없습니다.</div>
                    {isSuperuser && <div style={{ fontSize: '13px', marginTop: '4px' }}>우측 상단 "새 파트너"로 추가하세요.</div>}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      </div>

      <ContractorModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingContractor(null); }}
        contractor={editingContractor}
      />

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="파트너 삭제"
        width={400}
        footer={
          <>
            <Pill variant="ghost" onClick={() => setConfirmDelete(null)}>취소</Pill>
            <Pill variant="danger" onClick={handleDelete}>삭제</Pill>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: '14px', color: ui.text, lineHeight: 1.6 }}>
          <b>{confirmDelete?.name}</b> 파트너를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
        </p>
      </Modal>
    </div>
  );
};

export default ContractorManagement;
